import { Link } from "react-router";
import { Briefcase } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/states";
import { cn } from "@/lib/utils";
import { formatDateOnly } from "@/utils/dateUtils";
import { useAgenciaVagas } from "@/hooks/useAgenciaVagas";
import { vagaStatusBarClass, vagaStatusLabel } from "./timeline/agenciaVagaStatus";

interface AgenciaVagasTabProps {
  agenciaId: string;
}

/** Aba "Vagas": todas as vagas do contrato com a agência, com status e link para o detalhe. */
export function AgenciaVagasTab({ agenciaId }: AgenciaVagasTabProps) {
  const {
    data,
    isLoading,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useAgenciaVagas(agenciaId);

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-2 py-6 text-center">
        <p className="text-sm text-muted-foreground">Não foi possível carregar as vagas.</p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Tentar de novo
        </Button>
      </div>
    );
  }

  const vagas = data?.pages.flatMap((pagina) => pagina.linhas) ?? [];

  if (!vagas.length) {
    return (
      <EmptyState
        icon={Briefcase}
        title="Nenhuma vaga registrada ainda"
        description="As vagas abertas com essa agência aparecem aqui."
      />
    );
  }

  return (
    <div className="space-y-3">
      <ul className="divide-y rounded-md border">
        {vagas.map((vaga) => (
          <li key={vaga.id}>
            <Link
              to={`/vagas/${vaga.id}`}
              className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm hover:bg-accent/40"
            >
              <div className="flex min-w-0 items-center gap-2">
                <span
                  className={cn("h-2.5 w-2.5 shrink-0 rounded-full", vagaStatusBarClass(vaga.status))}
                  aria-hidden
                />
                <span className="truncate font-medium">{vaga.titulo}</span>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <span className="text-xs text-muted-foreground">
                  Criada em {formatDateOnly(vaga.criada_em)}
                </span>
                <Badge variant="outline">{vagaStatusLabel(vaga.status)}</Badge>
              </div>
            </Link>
          </li>
        ))}
      </ul>
      {hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? "Carregando..." : "Mostrar mais"}
          </Button>
        </div>
      )}
    </div>
  );
}

export default AgenciaVagasTab;
